import { useState } from "react";
import { format, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { createLocalDate } from "@/utils/dateUtils";
import { calcularEstadoProjeto } from "@/utils/controle-projeto-estado";
import type { ControleProjeto } from "@/types/controle-projeto";

type ModoFinalizacao = "vencido" | "antecipado" | "fixo";
type Termino = "na_data" | "antes";

interface FinalizarProjetoDialogProps {
  projeto: ControleProjeto | null;
  onClose: () => void;
  /** `novaDataConclusao` só vem quando o término muda (fixo ou encerrado antes da data). */
  onConfirm: (projeto: ControleProjeto, novaDataConclusao?: Date) => void;
}

function modoDe(projeto: ControleProjeto): ModoFinalizacao {
  const { estado } = calcularEstadoProjeto(projeto);
  if (estado === "fixo") return "fixo";
  if (estado === "vencido") return "vencido";
  return "antecipado";
}

// Carga, disponibilidade e pesquisas seguem a data de conclusão, não o status: fixo sem
// data ficaria alocado pra sempre, e quem sai antes precisa da data real gravada.
// O pai remonta o dialog por projeto (key), então o estado inicial vem das props.
export function FinalizarProjetoDialog({ projeto, onClose, onConfirm }: FinalizarProjetoDialogProps) {
  const hoje = startOfDay(new Date());
  const dataInicio = projeto?.data_inicio ? createLocalDate(projeto.data_inicio) : null;
  const dataConclusao = projeto?.data_conclusao ? createLocalDate(projeto.data_conclusao) : null;
  const modo = projeto ? modoDe(projeto) : "vencido";

  const diaDesabilitado = (dia: Date) => {
    if (dataInicio && isBefore(dia, dataInicio)) return true;
    if (modo === "antecipado" && dataConclusao && !isBefore(dia, dataConclusao)) return true;
    return false;
  };

  const [termino, setTermino] = useState<Termino>("na_data");
  const [dataTermino, setDataTermino] = useState<Date | undefined>(() =>
    diaDesabilitado(hoje) ? undefined : hoje,
  );

  const pedeData = modo === "fixo" || (modo === "antecipado" && termino === "antes");

  const handleConfirm = () => {
    if (!projeto) return;
    onConfirm(projeto, pedeData ? dataTermino : undefined);
  };

  const nomes = projeto?.freelancer?.nome
    ? `O projeto de ${projeto.freelancer.nome} com ${projeto.agencia?.nome ?? "a agência"}`
    : "O projeto";
  const conclusaoFormatada = dataConclusao ? format(dataConclusao, "dd/MM/yyyy") : "";

  return (
    <AlertDialog open={projeto !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Finalizar projeto?</AlertDialogTitle>
          <AlertDialogDescription>
            {modo === "vencido" &&
              `${nomes} vai para o histórico. Se precisar reabrir, edite a data de conclusão para uma data futura.`}
            {modo === "antecipado" &&
              `${nomes} está previsto para terminar em ${conclusaoFormatada}. Até quando o freelancer trabalha?`}
            {modo === "fixo" &&
              `${nomes} é fixo, sem data de conclusão. Informe o último dia de trabalho para liberar a agenda do freelancer.`}
            {(projeto?.planejados?.length ?? 0) > 0 && (
              <span className="mt-2 block font-medium text-foreground">
                {projeto?.planejados?.length === 1
                  ? "O período planejado também é cancelado."
                  : `Os ${projeto?.planejados?.length} períodos planejados também são cancelados.`}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {modo === "antecipado" && (
          <RadioGroup value={termino} onValueChange={(valor) => setTermino(valor as Termino)} className="gap-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem value="na_data" id="termino-na-data" className="mt-0.5" />
              <Label htmlFor="termino-na-data" className="font-normal leading-snug">
                Até {conclusaoFormatada}, só não vai renovar
                <span className="block text-xs text-muted-foreground">
                  Continua em andamento e vai para o histórico quando a data passar.
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="antes" id="termino-antes" className="mt-0.5" />
              <Label htmlFor="termino-antes" className="font-normal leading-snug">
                Termina antes da data prevista
                <span className="block text-xs text-muted-foreground">
                  Muda a data de conclusão para o último dia de trabalho.
                </span>
              </Label>
            </div>
          </RadioGroup>
        )}

        {pedeData && (
          <div className="space-y-2">
            <Label>Último dia de trabalho</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !dataTermino && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dataTermino ? format(dataTermino, "dd/MM/yyyy", { locale: ptBR }) : "Selecione a data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dataTermino}
                  onSelect={setDataTermino}
                  disabled={diaDesabilitado}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={pedeData && !dataTermino}>
            Finalizar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
